import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Modal dialog.
 *
 * Uses a real `<dialog>` so the browser handles the top layer, the backdrop and
 * focus trapping rather than a hand-rolled approximation that leaks tab focus
 * to the page behind it.
 */
export default function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 520,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  if (!open) return null

  return createPortal(
    <dialog
      ref={ref}
      // Esc fires `cancel`, not a click, so it has to be handled explicitly or
      // the dialog closes while React still thinks it's open.
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        // Clicks on the backdrop land on the dialog element itself.
        if (event.target === ref.current) onClose()
      }}
      style={{
        width: `min(${width}px, calc(100vw - 32px))`,
        maxHeight: 'calc(100vh - 64px)',
        padding: 0,
        border: 'var(--hairline-width) solid var(--hairline)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--panel)',
        color: 'var(--text-primary)',
        boxShadow: 'var(--shadow-pop)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 64px)' }}>
        <header
          className="spread"
          style={{
            padding: '16px 18px',
            borderBottom: 'var(--hairline-width) solid var(--hairline)',
          }}
        >
          <h2 className="t-display-s">{title}</h2>
          <button className="btn btn-quiet" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div style={{ padding: 18, overflow: 'auto' }}>{children}</div>

        {footer && (
          <footer
            className="row"
            style={{
              padding: '14px 18px',
              gap: 10,
              justifyContent: 'flex-end',
              borderTop: 'var(--hairline-width) solid var(--hairline)',
              background: 'var(--panel)',
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </dialog>,
    document.body,
  )
}
